import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Image, X, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  // Auto-open photo if passed in URL
  useEffect(() => {
    const photoId = searchParams.get('photo');
    if (photoId && photos.length > 0) {
      const photo = photos.find(p => p.id === photoId);
      if (photo) {
        setSelectedPhoto(photo);
      }
    }
  }, [searchParams, photos]);

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/photos?limit=100`);
      setPhotos(res.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const GlassCard = ({ children, className = '' }) => (
    <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl ${className}`}>
      {children}
    </div>
  );

  return (
    <div className="py-4">
      <div className="max-w-6xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 
            className="text-2xl sm:text-3xl font-black uppercase tracking-wider mb-1 flex items-center gap-2"
            style={{ color: theme.primary, textShadow: theme.glow }}
            data-testid="gallery-title"
          >
            <Image className="w-6 h-6 sm:w-8 sm:h-8" />
            Photo Gallery
          </h1>
          <p className="text-sm text-gray-400">Moments from DJ808 & CRG Productions</p>
        </div>

        {/* Gallery Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
        >
          {photos.map((photo, index) => (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setSelectedPhoto(photo)}
              className="aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-opacity-50 transition-all duration-300 cursor-pointer group relative"
              style={{ '--hover-border': theme.primary }}
              data-testid={`photo-${photo.id}`}
            >
              <img 
                src={photo.image_url} 
                alt={photo.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <ZoomIn className="w-8 h-8" style={{ color: theme.primary }} />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {photos.length === 0 && !loading && (
          <GlassCard className="p-16 text-center">
            <Image className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-500 text-lg">No photos yet</p>
            <p className="text-gray-600 text-sm mt-2">Check back soon for updates!</p>
          </GlassCard>
        )}

        {/* Lightbox */}
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="max-w-4xl bg-black/90 border-white/10 p-0">
            {selectedPhoto && (
              <div className="relative">
                <img 
                  src={selectedPhoto.image_url} 
                  alt={selectedPhoto.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <h3 className="font-bold text-lg" style={{ color: theme.primary }}>{selectedPhoto.title}</h3>
                  {selectedPhoto.description && (
                    <p className="text-gray-300 text-sm mt-1">{selectedPhoto.description}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Gallery;
